import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  DateString,
  getDayOfMonth,
  getDayOfWeek,
  getMonthName,
  getYear,
  todayDate,
} from '../../../../common/src/date_string.ts';
import { OrgType, WeeklySingingPlan } from '../../../../common/src/model.ts';
import {
  churchSingingPlanSkipDays,
  isSingingPlanForDate,
  schoolSingingPlanSkipDays,
} from '../../../../common/src/org.ts';
import { Pages } from '../../../../common/src/pages.ts';
import { LocalStorageKey } from '../../data/client_local_storage.ts';
import { useCurrentOrg } from '../../data/use_current_org.tsx';
import { useHousehold } from '../../data/use_household.tsx';
import { useLocalStorage } from '../../data/use_local_storage.tsx';
import { allSongsCompletedToday } from '../../data/use_singing_plan_completions.ts';
import { getUrlForPage } from '../../util/path.ts';
import { OuterPageContent } from '../../util/shared.tsx';
import { useCurrentDate } from '../../util/use_current_date.tsx';
import { withSearchParams } from '../../util/use_location.tsx';
import { useShowTutorialsButton } from '../../util/use_show_tutorials_button.ts';
import { ChurchRequired } from '../church_required.tsx';
import { Footer } from '../footer.tsx';
import { Header } from '../header.tsx';
import { ActivateSearchQueryString } from '../onboarding/common.tsx';
import { SubscriptionBanner } from '../subscription_banner.tsx';
import { HouseholdSingingPlanStreak } from './household_singing_plan_streak.tsx';
import { SingingPlanTile } from './singing_plan_tile.tsx';
import { ThisSundayTile } from './this_sunday_tile.tsx';

export const TodayPage = () => {
  // TODO(hewitt): display Christian holiday name if appropriate (e.g. "Christmas Day 2024 AD", "Easter Sunday 2024 AD")
  const today = useCurrentDate();
  const title = `${getMonthName(today)} ${getDayOfMonth(today)}, ${getYear(today)} AD`;
  const navigate = useNavigate();
  const { currentOrg, householdOrgs } = useCurrentOrg();
  const { household } = useHousehold();
  const allSongsCompleted = allSongsCompletedToday(household, householdOrgs, today);
  const [allSongsPreviouslyComplete] = useState(() => allSongsCompleted);
  const [currentDate, setCurrentDate] = useState<string>(() => today);
  const atLeastOneSingingPlan = !!householdOrgs.find(({ singingPlan }) => singingPlan);
  const [isStreakVisible, setIsStreakVisible] = useState<boolean>(() => allSongsCompleted && atLeastOneSingingPlan);
  const [isReviewTextVisible, setIsReviewTextVisible] = useState<boolean>(true);
  const [_, setMostRecentTodayTabVisit] = useLocalStorage(LocalStorageKey.MostRecentTodayTabVisit);
  const { onTutorialsClick } = useShowTutorialsButton();

  useEffect(() => {
    // transitioning from not completed to completed - hide review text
    // logic is too complex to analyze right now
    /* eslint-disable @eslint-react/set-state-in-effect */
    if (!allSongsPreviouslyComplete && allSongsCompleted) {
      setIsReviewTextVisible(false);
      setIsStreakVisible(true);
    } else if (allSongsPreviouslyComplete && !allSongsCompleted) {
      setIsStreakVisible(false);
    }
    /* eslint-enable @eslint-react/set-state-in-effect */
  }, [allSongsCompleted, allSongsPreviouslyComplete, setIsReviewTextVisible, setIsStreakVisible]);

  const navigateToDate = useCallback(
    (date: DateString) => {
      setCurrentDate(date);
      setIsStreakVisible(false);
      setIsReviewTextVisible(true);
    },
    [setCurrentDate, setIsStreakVisible],
  );

  const onSearchIconClick = useCallback(async () => {
    await navigate(withSearchParams(getUrlForPage(Pages.Organization), ActivateSearchQueryString));
  }, [navigate]);

  const school = householdOrgs.find(({ type }) => type === OrgType.School);
  const church = householdOrgs.find(({ type }) => type !== OrgType.School);

  const dayOfWeek = getDayOfWeek(today);
  const isSchoolVisible = school && !schoolSingingPlanSkipDays.includes(dayOfWeek);
  const isChurchVisible = church && !churchSingingPlanSkipDays.includes(dayOfWeek);

  const getSingingPlanTile = ({ title, singingPlan }: { title: string; singingPlan?: WeeklySingingPlan }) => {
    return (
      <SingingPlanTile
        title={title}
        singingPlan={singingPlan}
        currentDate={currentDate}
        setIsStreakVisible={setIsStreakVisible}
      />
    );
  };

  useEffect(() => {
    if (isSingingPlanForDate(householdOrgs, todayDate())) {
      setMostRecentTodayTabVisit(todayDate());
    }
    // eslint-disable-next-line
  }, []);

  return (
    <OuterPageContent>
      <Header title={title} onSearchClick={onSearchIconClick} onTutorialsClick={onTutorialsClick} showProfileAvatar />
      <SubscriptionBanner />
      <TileBackground>
        {!currentOrg ? (
          <ChurchRequired />
        ) : (
          <>
            {isStreakVisible && (
              <HouseholdSingingPlanStreak navigateToDate={navigateToDate} isReviewTextVisible={isReviewTextVisible} />
            )}
            {!isStreakVisible &&
              isSchoolVisible &&
              getSingingPlanTile({ title: 'School Songs', singingPlan: school.singingPlan })}
            {!isStreakVisible &&
              isChurchVisible &&
              getSingingPlanTile({ title: 'Family Worship', singingPlan: church.singingPlan })}
            <ThisSundayTile />
          </>
        )}
      </TileBackground>
      <Footer />
    </OuterPageContent>
  );
};

const TileBackground = styled.div`
  font-size: min(max(4.5vw, 0.9em), 1.5em);
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  width: 100%;
  overflow-y: auto;
  overflow-x: auto;
  background-color: var(--color-background-search);
  text-align: left;
`;
